import { useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Search } from "lucide-react";
import { exportXlsx } from "@/lib/exportXlsx";
import { useMe } from "@/components/app/MeContext";
import { ColumnsPanel } from "@/components/app/ColumnsPanel";
import { useTablePrefs } from "@/hooks/useTablePrefs";

const PAGE = 50;
const MIN_W = 60;
const fixed = (w) => (w ? { width: w, minWidth: w, maxWidth: w } : undefined);

// tableId set: per-user resize / reorder / show-hide columns (saved on the server)
function useLayout(tableId, columns) {
  const { layout, setLayout, reset } = useTablePrefs(tableId, columns);
  const byKey = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns]);
  const shown = useMemo(() => (layout ? layout.filter((l) => l.visible && byKey[l.key]).map((l) => ({ ...byKey[l.key], _w: l.width })) : columns), [layout, byKey, columns]);
  const labels = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, typeof c.label === "string" ? c.label : c.key])), [columns]);
  const setWidth = (key, w) => setLayout(layout.map((l) => (l.key === key ? { ...l, width: Math.max(MIN_W, Math.round(w)) } : l)));
  const moveKey = (from, to) => {
    if (from === to) return;
    const n = [...layout];
    const i = n.findIndex((l) => l.key === from);
    const j = n.findIndex((l) => l.key === to);
    const [x] = n.splice(i, 1);
    n.splice(j, 0, x);
    setLayout(n);
  };
  return { layout, setLayout, reset, shown, labels, setWidth, moveKey };
}

function Resizer({ th, onDone, testid }) {
  const start = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX, w0 = th.current.getBoundingClientRect().width;
    let w = w0;
    const mv = (ev) => { w = Math.max(MIN_W, w0 + ev.clientX - x0); th.current.style.width = th.current.style.minWidth = th.current.style.maxWidth = `${w}px`; };
    const up = () => { document.removeEventListener("mousemove", mv); document.removeEventListener("mouseup", up); onDone(w); };
    document.addEventListener("mousemove", mv);
    document.addEventListener("mouseup", up);
  };
  return <span onMouseDown={start} onClick={(e) => e.stopPropagation()} className="absolute right-0 top-0 hidden h-full w-1.5 cursor-col-resize hover:bg-sky-300 md:block" data-testid={testid} />;
}

function HeadCell({ c, sort, toggle, custom, lay, drag, setDrag, testid }) {
  const th = useRef(null);
  const resized = useRef(false);
  return (
    <th ref={th} onClick={() => { if (!resized.current) { toggle(c.key); } }} style={fixed(c._w)} draggable={custom} onDragStart={(e) => { e.dataTransfer.setData("text/plain", c.key); setDrag(c.key); }} onDragOver={(e) => custom && e.preventDefault()}
      onDrop={() => { if (custom && drag) { lay.moveKey(drag, c.key); } setDrag(null); }} data-testid={custom ? `${testid}-th-${c.key}` : undefined}
      className={`relative cursor-pointer select-none whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-900 ${c.right ? "text-right" : "text-left"} ${c._w ? "truncate" : ""} ${drag === c.key ? "opacity-50" : ""}`}>
      <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
      {custom && <Resizer th={th} onDone={(w) => { resized.current = true; setTimeout(() => { resized.current = false; }, 50); lay.setWidth(c.key, w); }} testid={`${testid}-resize-${c.key}`} />}
    </th>
  );
}

const cellText = (c, r) => { const v = c.exp ? c.exp(r) : c.value ? c.value(r) : r[c.key]; return v == null || typeof v === "object" ? undefined : String(v); };

// Register-style table: search in results, click a header to sort, 50 rows per page.
export function DataTable({ columns: allCols, rows, onRowClick, testid = "data-table", rowId = (r) => r.id, empty = "No records", toolbar, exportName: name, exportPerm, selected, onSelect, rowClickable, tableId }) {
  const { can } = useMe() || { can: () => true };
  const lay = useLayout(tableId, allCols);
  const columns = lay.shown;
  const custom = !!tableId && !!lay.layout;
  const [drag, setDrag] = useState(null);
  const exportName = exportPerm && !can(exportPerm) ? null : name;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(0);
  const list = useMemo(() => {
    let r = rows || [];
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => allCols.some((c) => String(c.value ? c.value(x) : x[c.key] ?? "").toLowerCase().includes(s)));
    }
    if (sort) {
      const col = allCols.find((c) => c.key === sort.key);
      const val = (x) => (col.value ? col.value(x) : x[col.key]);
      r = [...r].sort((a, b) => {
        const x = val(a), y = val(b);
        if (x === y) return 0;
        if (x == null || x === "") return 1;
        if (y == null || y === "") return -1;
        return (x > y ? 1 : -1) * (sort.dir === "asc" ? 1 : -1);
      });
    }
    return r;
  }, [rows, q, sort, allCols]);
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const cur = list.slice(page * PAGE, page * PAGE + PAGE);
  const toggle = (k) => setSort((s) => (s?.key === k ? (s.dir === "asc" ? { key: k, dir: "desc" } : null) : { key: k, dir: "asc" }));
  const sel = !!onSelect && !!selected;
  const allOn = sel && list.length > 0 && list.every((r) => selected.has(rowId(r)));
  const flip = (ids, on) => { const n = new Set(selected); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); onSelect(n); };
  const box = "h-4 w-4 accent-sky-700 cursor-pointer";
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/70 px-3 py-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <input className="h-8 w-full rounded border border-slate-200 bg-white pl-7 pr-2 text-xs outline-none focus:border-sky-600" placeholder="Search in results…"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} data-testid={`${testid}-search`} />
        </div>
        <span className="text-xs text-slate-500" data-testid={`${testid}-count`}>{list.length} records</span>
        <div className="ml-auto flex flex-wrap gap-2">{toolbar}
          {custom && <ColumnsPanel layout={lay.layout} labels={lay.labels} onChange={lay.setLayout} onReset={lay.reset} testid={testid} />}
          {exportName && <button onClick={() => exportXlsx(exportName, columns, list)} disabled={!list.length} data-testid={`${testid}-export`}
            className="inline-flex h-8 items-center gap-1 rounded border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"><Download className="h-3.5 w-3.5" />Export Excel</button>}
        </div>
      </div>
      <div className="max-h-[68vh] overflow-auto">
        <table className={`w-full text-xs ${custom ? "table-auto" : ""}`}>
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr>{sel && <th className="w-8 px-3 py-2"><input type="checkbox" className={box} checked={allOn} onChange={() => flip(list.map(rowId), !allOn)} title="Select all in results" data-testid={`${testid}-select-all`} /></th>}{columns.map((c) => (
              <HeadCell key={c.key} c={c} sort={sort} toggle={toggle} custom={custom} lay={lay} drag={drag} setDrag={setDrag} testid={testid} />))}
            </tr>
          </thead>
          <tbody>
            {cur.map((r, i) => (
              <tr key={rowId(r)} onClick={() => onRowClick?.(r)} data-testid={`${testid}-row-${r.order_no || r.dispatch_no || r.movement_no || r.code || rowId(r)}`}
                className={`border-b border-slate-100 ${onRowClick && (!rowClickable || rowClickable(r)) ? "cursor-pointer hover:bg-sky-50/60" : ""} ${sel && selected.has(rowId(r)) ? "bg-sky-50" : i % 2 ? "bg-slate-50/40" : ""}`}>
                {sel && <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" className={box} checked={selected.has(rowId(r))} onChange={(e) => flip([rowId(r)], e.target.checked)} data-testid={`${testid}-select-${rowId(r)}`} /></td>}
                {columns.map((c) => (
                  <td key={c.key} style={fixed(c._w)} title={c._w ? cellText(c, r) : undefined} className={`px-3 py-2 text-slate-700 ${c._w ? "truncate" : c.wrap ? "max-w-[260px] truncate" : "whitespace-nowrap"} ${c.right ? "text-right font-mono" : ""} ${c.mono ? "font-mono" : ""}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? <span className="text-slate-300">—</span>)}
                  </td>
                ))}
              </tr>
            ))}
            {!cur.length && <tr><td colSpan={columns.length + (sel ? 1 : 0)} className="px-3 py-10 text-center text-sm text-slate-400" data-testid={`${testid}-empty`}>{empty}</td></tr>}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-1.5 text-xs text-slate-600">
          <button className="rounded p-1 hover:bg-slate-100 disabled:opacity-40" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></button>
          Page {page + 1} / {pages}
          <button className="rounded p-1 hover:bg-slate-100 disabled:opacity-40" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}
